/**
 * Routing slice. A small extra store rather than another bag of fields on the
 * main one, because routes have their own life cycle (import / export / edit)
 * and bundling them with the chart-display state would just be noise.
 */
import { create } from 'zustand';
import type { Route, Waypoint } from '@/routing';
import {
  monitorRoute,
  followLeg,
  clampLegIndex,
  clampFollowLegIndex,
  approachLeg,
  APPROACH_LEG_INDEX,
  legCount,
  DEFAULT_ARRIVAL_RADIUS_NM,
} from '@/routing';
import { rhumbBearingDeg, rhumbDistanceNm, distanceToRun } from '@/nav';
import { loadJson, saveJson } from './persist';

interface RoutingState {
  routes: Route[];
  activeRouteId: string | null;
  /**
   * The route we are actively building, if any. While set, long-pressing the
   * chart appends to it; finishing clears it so a later long-press starts a
   * fresh route rather than extending the finished one.
   */
  buildingRouteId: string | null;
  /**
   * True right after "New route": the instruction on screen asks the skipper to
   * long-press the chart to drop the first waypoint. While this is set, that
   * long-press starts the route instead of opening the open-water radial ring.
   * Consumed by the first waypoint drop, so it's a one-shot, not a mode.
   */
  awaitingRouteStart: boolean;
  setAwaitingRouteStart: (v: boolean) => void;
  /** Insert / replace a route. */
  upsertRoute: (route: Route) => void;
  removeRoute: (id: string) => void;
  clearRoutes: () => void;
  setActiveRoute: (id: string | null) => void;
  /** Route ids hidden from the chart. Empty = all routes shown. */
  hiddenRouteIds: string[];
  /** Show/hide a route on the chart (the followed route always stays shown). */
  toggleRouteHidden: (id: string) => void;
  /** Append a waypoint to a specific route, or to the active one. */
  addWaypoint: (routeId: string | null, wp: Waypoint) => void;
  removeWaypoint: (routeId: string, wpId: string) => void;
  renameRoute: (id: string, name: string) => void;
  /**
   * Reverse a route's waypoint order so the passage can be run back the other
   * way. Per-waypoint data (names, arrival radius, notes) travels with its point
   * for free. Per-leg data (legXtd / legNotes), keyed by the leg's START
   * waypoint, is re-keyed so each attribute stays on the same physical leg.
   */
  reverseRoute: (id: string) => void;
  /**
   * Deep-copy a route to a new, fully independent one (new route id, fresh
   * waypoint ids, all leg/waypoint data copied and re-keyed to the new ids),
   * suffix its name, and select the copy. The original is left untouched.
   */
  duplicateRoute: (id: string) => void;
  /**
   * Add route(s) parsed from an imported GPX file. Each incoming route is given a
   * fresh route id and fresh waypoint ids (with its per-leg data re-keyed to
   * match), so an import never collides with or overwrites an existing route, and
   * the original file is untouched. The first imported route is selected. Returns
   * the routes as they were actually stored (with their new ids) so the caller can
   * report what came in.
   */
  importRoutes: (routes: Route[]) => Route[];

  // --- Build flow ---
  /** Enter (id) or leave (null) build mode for a route. */
  setBuildingRoute: (id: string | null) => void;
  /** Stop building: a later long-press starts a new route, not this one. */
  finishRoute: () => void;
  /** The route being followed (active-leg line + live data boxes), or null. */
  followRouteId: string | null;
  /**
   * The leg we are actively steering (0-based: waypoint[i] → waypoint[i+1]) once
   * a route is activated for follow, or null when we're not following a route this
   * way (legacy follow / goto). This is the AUTHORITATIVE active leg: the arrival
   * sequencer advances it, and the HUD, the active-leg highlight and the off-track
   * check all read it, so which leg we're on is one stored fact rather than
   * re-derived by nearest-approach every render. null falls back to the older
   * nearest-leg behaviour so restored state from an earlier build still follows.
   */
  activeLegIndex: number | null;
  /**
   * Activate ("Go") a route for live follow, starting on leg 1 (WP1 → WP2). Puts
   * the app into route-follow mode for this route, stores the active leg, and
   * clears any goto/build. A route with fewer than two waypoints has nothing to
   * follow, so this is a no-op for it. Only one route is active at a time.
   */
  activateRoute: (id: string) => void;
  /** Activate a route from a chosen leg (the leg out of the picked waypoint). */
  activateRouteFrom: (id: string, legIndex: number) => void;
  /** Stop following: leave route-follow mode and clear the active leg. */
  deactivateRoute: () => void;
  /** Roll the active leg on to the next one (the arrival sequencer's advance). */
  advanceActiveLeg: () => void;
  /**
   * Step the active leg by a signed delta (the follow HUD's Previous/Next buttons),
   * so the skipper can skip a waypoint forward or drop back to an earlier one by
   * hand. Clamped to real legs, and a no-op unless a route is being followed. The
   * arrival sequencer picks up from wherever this leaves the leg.
   */
  stepActiveLeg: (delta: number) => void;
  /** Start following a route: draws the active leg and feeds the data boxes. */
  followRoute: (id: string) => void;
  /**
   * Leg index to follow from when the user picks "Follow from this waypoint".
   * null = follow from wherever the boat is (the normal behaviour).
   */
  followFromLegIndex: number | null;
  /** Follow a route from a chosen leg (the leg out of the picked waypoint). */
  followRouteFrom: (id: string, legIndex: number) => void;
  /** Stop following the route. */
  stopFollowing: () => void;
  /**
   * Drop a waypoint as part of route building. Appends to the route being
   * built; if none is being built, starts a new route and enters build mode.
   */
  dropRouteWaypoint: (position: [number, number], name?: string) => void;
  /** Move, rename or re-icon a single waypoint (chart drag, or the editor). */
  updateWaypoint: (
    routeId: string,
    wpId: string,
    patch: Partial<Pick<Waypoint, 'position' | 'name' | 'icon' | 'description' | 'arrivalRadiusNm' | 'arrivalAlarm'>>,
  ) => void;
  /** Insert a waypoint at an index in a route, splitting that leg in two. */
  insertWaypoint: (routeId: string, index: number, wp: Waypoint) => void;
  /** Patch route-level fields (name, colour, route-wide proximity radius, default XTD). */
  updateRoute: (
    routeId: string,
    patch: Partial<Pick<Route, 'name' | 'notes' | 'arrivalRadiusNm' | 'xtdNm' | 'color'>>,
  ) => void;
  /** Set or clear a per-leg XTD override, keyed by the leg's start waypoint id. */
  setLegXtd: (routeId: string, fromId: string, xtdNm: number | null) => void;
  /** Set or clear a per-leg free-text note, keyed by the leg's start waypoint id. */
  setLegNote: (routeId: string, fromId: string, note: string | null) => void;

  // --- Standalone marks (waypoints not part of a route) ---
  marks: Waypoint[];
  addMark: (wp: Waypoint) => void;
  removeMark: (id: string) => void;
  updateMark: (id: string, patch: Partial<Pick<Waypoint, 'position' | 'name' | 'icon' | 'description' | 'arrivalRadiusNm' | 'arrivalAlarm'>>) => void;

  // --- Go to a single mark (direct goto, the follow machinery for one point) ---
  goto: { markId: string; from: [number, number] } | null;
  gotoMark: (markId: string, from: [number, number]) => void;
  stopGoto: () => void;

  // --- Passage destination (the target a planned route is made for) ---
  /**
   * Where the passage is heading. Set from "Set as destination" on a chart tap;
   * the route wizard plots a route TO this point. Distinct from a waypoint or a
   * mark, and drawn with its own destination-anchor symbol. One at a time.
   */
  destination: { position: [number, number]; name?: string } | null;
  setDestination: (position: [number, number], name?: string) => void;
  clearDestination: () => void;
}

/**
 * Re-key a per-leg map (legXtd / legNotes) through a key translation. Leg data
 * is keyed by the leg's start waypoint id; when a route is reversed or copied,
 * that key changes but the value must stay on the same physical leg. Entries
 * whose key doesn't map (translate returns undefined) are dropped, which never
 * happens for a well-formed route but keeps a stray key from surviving on a leg
 * that no longer exists.
 */
function rekeyLegData<T>(
  data: Record<string, T> | undefined,
  translate: (oldKey: string) => string | undefined,
): Record<string, T> | undefined {
  if (!data) return undefined;
  const out: Record<string, T> = {};
  for (const [k, v] of Object.entries(data)) {
    const nk = translate(k);
    if (nk !== undefined) out[nk] = v;
  }
  return out;
}

let nextWpId = 1;
let nextRouteId = 1;
export function nextWaypointId(): string {
  return `wp-${nextWpId++}`;
}
export function nextRouteIdFn(): string {
  return `r-${nextRouteId++}`;
}

/**
 * Re-id a route onto fresh, app-local ids: a new route id, a new id for every
 * waypoint, and the per-leg data (keyed by the leg's start waypoint id) re-keyed
 * to the new ids. Used on GPX import so a route parsed with the parser's own
 * placeholder ids joins the store as a first-class route that shares no ids with
 * anything already loaded. Positions are copied into fresh arrays so an edit to
 * the import can't reach back through a shared reference. createdAt is kept if the
 * file carried one, else stamped now.
 */
function reidRoute(route: Route): Route {
  const idMap: Record<string, string> = {};
  const waypoints = route.waypoints.map((w) => {
    const newId = nextWaypointId();
    idMap[w.id] = newId;
    return { ...w, id: newId, position: [w.position[0], w.position[1]] as [number, number] };
  });
  const legXtd = rekeyLegData(route.legXtd, (k) => idMap[k]);
  const legNotes = rekeyLegData(route.legNotes, (k) => idMap[k]);
  return {
    ...route,
    id: nextRouteIdFn(),
    waypoints,
    legXtd,
    legNotes,
    createdAt: route.createdAt ?? Date.now(),
  };
}

export const useRoutingStore = create<RoutingState>((set) => ({
  routes: [],
  activeRouteId: null,
  buildingRouteId: null,
  awaitingRouteStart: false,
  followRouteId: null,
  activeLegIndex: null,
  hiddenRouteIds: [],
  followFromLegIndex: null,
  marks: [],
  goto: null,

  upsertRoute: (route) =>
    set((s) => {
      const idx = s.routes.findIndex((r) => r.id === route.id);
      if (idx === -1) {
        // A brand-new route (GPX import, a planned passage, a set-destination
        // route) gets its created stamp here if it doesn't already carry one.
        const stamped = route.createdAt ? route : { ...route, createdAt: Date.now() };
        return { routes: [...s.routes, stamped], activeRouteId: s.activeRouteId ?? stamped.id };
      }
      const next = s.routes.slice();
      next[idx] = route;
      return { routes: next };
    }),

  removeRoute: (id) =>
    set((s) => ({
      routes: s.routes.filter((r) => r.id !== id),
      activeRouteId: s.activeRouteId === id ? null : s.activeRouteId,
      buildingRouteId: s.buildingRouteId === id ? null : s.buildingRouteId,
      followRouteId: s.followRouteId === id ? null : s.followRouteId,
      // Deleting the route we're following ends the follow, so its stored active
      // leg goes with it rather than pointing at a route that's gone.
      activeLegIndex: s.followRouteId === id ? null : s.activeLegIndex,
      followFromLegIndex: s.followRouteId === id ? null : s.followFromLegIndex,
      hiddenRouteIds: s.hiddenRouteIds.filter((x) => x !== id),
    })),

  clearRoutes: () =>
    set({ routes: [], activeRouteId: null, buildingRouteId: null, followRouteId: null, activeLegIndex: null, hiddenRouteIds: [], followFromLegIndex: null }),

  setActiveRoute: (activeRouteId) => set({ activeRouteId }),

  toggleRouteHidden: (id) =>
    set((s) => ({
      hiddenRouteIds: s.hiddenRouteIds.includes(id)
        ? s.hiddenRouteIds.filter((x) => x !== id)
        : [...s.hiddenRouteIds, id],
    })),

  addWaypoint: (routeId, wp) =>
    set((s) => {
      const targetId = routeId ?? s.activeRouteId;
      if (!targetId) {
        // No active route, create one.
        const newRoute: Route = {
          id: nextRouteIdFn(),
          name: 'New route',
          waypoints: [wp],
          createdAt: Date.now(),
          // Arrival alarms default ON for every waypoint of a new route
          // (fb-msmb9w95-b): a skipper who wants quiet at one point switches
          // that point off, rather than arming twelve points one by one.
          arrivalRadiusNm: DEFAULT_ARRIVAL_RADIUS_NM,
        };
        return { routes: [...s.routes, newRoute], activeRouteId: newRoute.id };
      }
      const idx = s.routes.findIndex((r) => r.id === targetId);
      if (idx === -1) return s;
      const next = s.routes.slice();
      next[idx] = { ...next[idx], waypoints: [...next[idx].waypoints, wp] };
      return { routes: next };
    }),

  removeWaypoint: (routeId, wpId) =>
    set((s) => {
      const idx = s.routes.findIndex((r) => r.id === routeId);
      if (idx === -1) return s;
      const route = s.routes[idx];
      const waypoints = route.waypoints.filter((w) => w.id !== wpId);
      // Deleting the last waypoint leaves nothing to follow, so drop the whole
      // route (and any state pointing at it), the same cleanup removeRoute does,
      // rather than keeping an empty route shell around.
      if (waypoints.length === 0) {
        return {
          routes: s.routes.filter((r) => r.id !== routeId),
          activeRouteId: s.activeRouteId === routeId ? null : s.activeRouteId,
          buildingRouteId: s.buildingRouteId === routeId ? null : s.buildingRouteId,
          followRouteId: s.followRouteId === routeId ? null : s.followRouteId,
          activeLegIndex: s.followRouteId === routeId ? null : s.activeLegIndex,
          followFromLegIndex: s.followRouteId === routeId ? null : s.followFromLegIndex,
          hiddenRouteIds: s.hiddenRouteIds.filter((x) => x !== routeId),
        };
      }
      // Removing a waypoint closes the gap between its neighbours, so the legs
      // re-link on their own. Drop any XTD corridor keyed to the leg that
      // started at the removed waypoint so it doesn't dangle on a leg that no
      // longer exists.
      let legXtd = route.legXtd;
      if (legXtd && wpId in legXtd) {
        legXtd = { ...legXtd };
        delete legXtd[wpId];
      }
      // The leg's note is keyed the same way, so drop it too when the leg goes.
      let legNotes = route.legNotes;
      if (legNotes && wpId in legNotes) {
        legNotes = { ...legNotes };
        delete legNotes[wpId];
      }
      const next = s.routes.slice();
      next[idx] = { ...route, waypoints, legXtd, legNotes };
      // If we're following this route, its legs just renumbered. Pin the stored
      // active leg back into range so it can't dangle past the new end (a fuller
      // remap isn't worth it: deleting a waypoint under an active follow is rare,
      // and clamping keeps us on a real leg rather than crashing or pointing off
      // the end).
      const activeLegIndex =
        s.followRouteId === routeId && s.activeLegIndex != null
          ? clampLegIndex(next[idx], s.activeLegIndex)
          : s.activeLegIndex;
      return { routes: next, activeLegIndex };
    }),

  renameRoute: (id, name) =>
    set((s) => ({
      routes: s.routes.map((r) => (r.id === id ? { ...r, name } : r)),
    })),

  reverseRoute: (id) =>
    set((s) => {
      const idx = s.routes.findIndex((r) => r.id === id);
      if (idx === -1) return s;
      const route = s.routes[idx];
      // Per-waypoint data (name, arrival radius, notes) rides along on each
      // waypoint object, so reversing the array carries it to the right place.
      const waypoints = route.waypoints.slice().reverse();
      // Per-leg data is keyed by the leg's START waypoint. The physical leg
      // between A and B survives reversal, but we now travel it B->A, so its
      // start flips from A to B. In the ORIGINAL order the waypoint after A is
      // B, so each leg's new key is the id of the waypoint that followed its old
      // start. Build that "next id in the original order" lookup and re-key with
      // it, and the XTD/notes stay on the same physical leg whichever way we run
      // it. (The original last waypoint had no leg keyed to it; the original
      // first never appears as a key after reversal, which balances out.)
      const ids = route.waypoints.map((w) => w.id);
      const nextId: Record<string, string> = {};
      for (let i = 0; i < ids.length - 1; i++) nextId[ids[i]] = ids[i + 1];
      const legXtd = rekeyLegData(route.legXtd, (k) => nextId[k]);
      const legNotes = rekeyLegData(route.legNotes, (k) => nextId[k]);
      const next = s.routes.slice();
      next[idx] = { ...route, waypoints, legXtd, legNotes };
      // If we're following the route we just reversed, keep the active leg on the
      // same physical stretch of water. Leg i (waypoint i → i+1) becomes, run the
      // other way, the leg between the same two points at the mirrored index
      // (legCount − 1 − i), so the boat carries on down the leg it was steering
      // rather than snapping to a different one.
      const n = legCount(next[idx]);
      const activeLegIndex =
        s.followRouteId === id && s.activeLegIndex != null && n > 0
          ? clampLegIndex(next[idx], n - 1 - s.activeLegIndex)
          : s.activeLegIndex;
      return { routes: next, activeLegIndex };
    }),

  duplicateRoute: (id) =>
    set((s) => {
      const idx = s.routes.findIndex((r) => r.id === id);
      if (idx === -1) return s;
      const route = s.routes[idx];
      // Fresh id for every waypoint so the copy shares no ids with the original.
      // Keep a map from old id to new so the leg data (keyed by waypoint id) can
      // be re-keyed to match. The waypoint order is unchanged, so each leg's
      // start is the same waypoint, just under its new id.
      const idMap: Record<string, string> = {};
      const waypoints = route.waypoints.map((w) => {
        const newId = nextWaypointId();
        idMap[w.id] = newId;
        // Copy the position tuple into a fresh array, so a drag on the copy can
        // never reach through a shared reference and move the original's point.
        return { ...w, id: newId, position: [w.position[0], w.position[1]] as [number, number] };
      });
      const legXtd = rekeyLegData(route.legXtd, (k) => idMap[k]);
      const legNotes = rekeyLegData(route.legNotes, (k) => idMap[k]);
      const copy: Route = {
        ...route,
        id: nextRouteIdFn(),
        name: `Copy of ${route.name}`,
        waypoints,
        legXtd,
        legNotes,
        createdAt: Date.now(),
      };
      // Append and select the copy; the original stays exactly as it was.
      return { routes: [...s.routes, copy], activeRouteId: copy.id };
    }),

  importRoutes: (incoming) => {
    // Re-id up front (pure, no state needed), then append in one set. Selecting
    // the first import makes it the active route so it shows straight away.
    const added = incoming.map(reidRoute);
    if (added.length === 0) return added;
    set((s) => ({
      routes: [...s.routes, ...added],
      activeRouteId: added[0].id,
      // An import isn't a build, and shouldn't disturb any follow in progress.
      buildingRouteId: null,
      awaitingRouteStart: false,
    }));
    return added;
  },

  setBuildingRoute: (buildingRouteId) => set({ buildingRouteId }),

  setAwaitingRouteStart: (awaitingRouteStart) => set({ awaitingRouteStart }),

  finishRoute: () => set({ buildingRouteId: null }),

  activateRoute: (id) =>
    set((s) => {
      const route = s.routes.find((r) => r.id === id);
      // Fewer than two waypoints is a single point, so there's no leg to steer.
      // Leave everything as it was rather than entering a follow with no target.
      if (!route || route.waypoints.length < 2) return s;
      return {
        followRouteId: id,
        activeRouteId: id,
        activeLegIndex: 0,
        buildingRouteId: null,
        goto: null,
        followFromLegIndex: null,
        hiddenRouteIds: s.hiddenRouteIds.filter((x) => x !== id),
      };
    }),

  activateRouteFrom: (id, legIndex) =>
    set((s) => {
      const route = s.routes.find((r) => r.id === id);
      if (!route || route.waypoints.length < 2) return s;
      return {
        followRouteId: id,
        activeRouteId: id,
        activeLegIndex: clampLegIndex(route, legIndex),
        buildingRouteId: null,
        goto: null,
        followFromLegIndex: null,
        hiddenRouteIds: s.hiddenRouteIds.filter((x) => x !== id),
      };
    }),

  deactivateRoute: () => set({ followRouteId: null, activeLegIndex: null, followFromLegIndex: null }),

  advanceActiveLeg: () =>
    set((s) => {
      if (!s.followRouteId || s.activeLegIndex == null) return s;
      const route = s.routes.find((r) => r.id === s.followRouteId);
      if (!route) return s;
      const n = legCount(route);
      // Roll on to the next leg, never past the last one. Finishing the final leg
      // is a "route complete" decision the runner makes (it calls deactivateRoute),
      // so here we simply refuse to walk off the end.
      const next = Math.min(s.activeLegIndex + 1, n - 1);
      if (next === s.activeLegIndex) return s;
      return { activeLegIndex: next };
    }),

  stepActiveLeg: (delta) =>
    set((s) => {
      // Only meaningful while following a route with a stored active leg.
      if (!s.followRouteId || s.activeLegIndex == null) return s;
      const route = s.routes.find((r) => r.id === s.followRouteId);
      if (!route) return s;
      // Previous stops one below leg 0, at the approach leg to WP1, so a route can
      // be picked up from wherever we are rather than always starting at WP2. Next
      // still can't walk off past the last leg; a no-move leaves state untouched.
      const next = clampFollowLegIndex(route, s.activeLegIndex + delta);
      if (next === s.activeLegIndex) return s;
      return { activeLegIndex: next };
    }),

  followRoute: (id) =>
    set((s) => {
      const route = s.routes.find((r) => r.id === id);
      // Legacy entry point kept for existing callers. When the route is followable
      // it now also seeds the authoritative active leg (leg 1), so follow behaves
      // the same however it was started; an unfollowable route leaves the index
      // null and the old nearest-leg display takes over.
      return {
        followRouteId: id,
        activeRouteId: id,
        activeLegIndex: route && route.waypoints.length >= 2 ? 0 : null,
        buildingRouteId: null,
        goto: null,
        followFromLegIndex: null,
        hiddenRouteIds: s.hiddenRouteIds.filter((x) => x !== id),
      };
    }),

  followRouteFrom: (id, legIndex) =>
    set((s) => {
      const route = s.routes.find((r) => r.id === id);
      return {
        followRouteId: id,
        activeRouteId: id,
        // Seed the authoritative active leg at the chosen leg when the route is
        // followable, so "Follow from this waypoint" sequences from there.
        activeLegIndex: route && route.waypoints.length >= 2 ? clampLegIndex(route, legIndex) : null,
        buildingRouteId: null,
        goto: null,
        followFromLegIndex: Math.max(0, legIndex),
        hiddenRouteIds: s.hiddenRouteIds.filter((x) => x !== id),
      };
    }),

  stopFollowing: () => set({ followRouteId: null, activeLegIndex: null, followFromLegIndex: null }),

  dropRouteWaypoint: (position, name) =>
    set((s) => {
      const buildingId = s.buildingRouteId;
      const idx = buildingId ? s.routes.findIndex((r) => r.id === buildingId) : -1;
      if (buildingId && idx !== -1) {
        const route = s.routes[idx];
        const wp: Waypoint = {
          id: nextWaypointId(),
          position,
          name: name ?? `WP${route.waypoints.length + 1}`,
        };
        const next = s.routes.slice();
        next[idx] = { ...route, waypoints: [...route.waypoints, wp] };
        return { routes: next, awaitingRouteStart: false };
      }
      // Nothing being built: start a fresh route and enter build mode.
      const newId = nextRouteIdFn();
      const wp: Waypoint = { id: nextWaypointId(), position, name: name ?? 'WP1' };
      const route: Route = { id: newId, name: 'Route', waypoints: [wp], createdAt: Date.now(), arrivalRadiusNm: DEFAULT_ARRIVAL_RADIUS_NM };
      return { routes: [...s.routes, route], activeRouteId: newId, buildingRouteId: newId, awaitingRouteStart: false };
    }),

  updateWaypoint: (routeId, wpId, patch) =>
    set((s) => {
      const idx = s.routes.findIndex((r) => r.id === routeId);
      if (idx === -1) return s;
      const route = s.routes[idx];
      const waypoints = route.waypoints.map((w) => (w.id === wpId ? { ...w, ...patch } : w));
      const next = s.routes.slice();
      next[idx] = { ...route, waypoints };
      return { routes: next };
    }),

  insertWaypoint: (routeId, index, wp) =>
    set((s) => {
      const idx = s.routes.findIndex((r) => r.id === routeId);
      if (idx === -1) return s;
      const route = s.routes[idx];
      const waypoints = route.waypoints.slice();
      const at = Math.max(0, Math.min(index, waypoints.length));
      waypoints.splice(at, 0, wp);
      const next = s.routes.slice();
      next[idx] = { ...route, waypoints };
      // Splitting a leg on the route we're following renumbers the legs from the
      // insertion point on. If the new point lands at or before the active leg,
      // that leg shifts one along, so bump the stored index to stay on the same
      // physical stretch; then clamp so it's always a real leg.
      let activeLegIndex = s.activeLegIndex;
      if (s.followRouteId === routeId && activeLegIndex != null) {
        if (at <= activeLegIndex) activeLegIndex += 1;
        activeLegIndex = clampLegIndex(next[idx], activeLegIndex);
      }
      return { routes: next, activeLegIndex };
    }),

  updateRoute: (routeId, patch) =>
    set((s) => ({ routes: s.routes.map((r) => (r.id === routeId ? { ...r, ...patch } : r)) })),

  setLegXtd: (routeId, fromId, xtdNm) =>
    set((s) => ({
      routes: s.routes.map((r) => {
        if (r.id !== routeId) return r;
        const legXtd = { ...(r.legXtd ?? {}) };
        if (xtdNm === null) delete legXtd[fromId];
        else legXtd[fromId] = xtdNm;
        return { ...r, legXtd };
      }),
    })),

  setLegNote: (routeId, fromId, note) =>
    set((s) => ({
      routes: s.routes.map((r) => {
        if (r.id !== routeId) return r;
        const legNotes = { ...(r.legNotes ?? {}) };
        if (note === null || note === '') delete legNotes[fromId];
        else legNotes[fromId] = note;
        return { ...r, legNotes };
      }),
    })),

  addMark: (wp) => set((s) => ({ marks: [...s.marks, wp] })),

  removeMark: (id) =>
    set((s) => ({
      marks: s.marks.filter((m) => m.id !== id),
      goto: s.goto?.markId === id ? null : s.goto,
    })),

  updateMark: (id, patch) =>
    set((s) => ({ marks: s.marks.map((m) => (m.id === id ? { ...m, ...patch } : m)) })),

  gotoMark: (markId, from) =>
    set({ goto: { markId, from }, followRouteId: null, activeLegIndex: null, followFromLegIndex: null }),

  stopGoto: () => set({ goto: null }),

  destination: null,
  setDestination: (position, name) => set({ destination: { position, name } }),
  clearDestination: () => set({ destination: null }),
}));

/* --- Persistence -------------------------------------------------------------
 * Routes and standalone marks are the skipper's own work, so they have to
 * survive an app kill/restart the same way the display settings do (Android
 * kills a backgrounded app hard). We mirror the settings pattern in ./persist:
 * load the saved slice on boot, write it back on every change. Before this the
 * routes and marks lived in memory only and vanished on reload, which also made
 * the ErrorBoundary's "your routes and marks are saved" panel a false promise.
 */
const ROUTING_LS = 'acp.routing.v1';

interface PersistedRouting {
  routes: Route[];
  marks: Waypoint[];
  activeRouteId: string | null;
  followRouteId: string | null;
  activeLegIndex: number | null;
  followFromLegIndex: number | null;
  hiddenRouteIds: string[];
  destination: { position: [number, number]; name?: string } | null;
}

function snapshotRouting(s: RoutingState): PersistedRouting {
  return {
    routes: s.routes,
    marks: s.marks,
    activeRouteId: s.activeRouteId,
    followRouteId: s.followRouteId,
    activeLegIndex: s.activeLegIndex,
    followFromLegIndex: s.followFromLegIndex,
    hiddenRouteIds: s.hiddenRouteIds,
    destination: s.destination,
  };
}

/** Pull the number out of a `wp-12` / `r-3` id, or 0 if it doesn't fit the shape. */
function idNum(id: string, prefix: string): number {
  if (typeof id !== 'string' || !id.startsWith(prefix)) return 0;
  const n = Number(id.slice(prefix.length));
  return Number.isFinite(n) ? n : 0;
}

/**
 * After restoring saved routes/marks the id counters have to jump past every id
 * we just loaded. If they didn't, the next new waypoint or route would reuse an
 * id that's already on the chart and quietly overwrite the restored one.
 */
function seedIdCounters(routes: Route[], marks: Waypoint[]): void {
  let maxWp = 0;
  let maxRoute = 0;
  for (const r of routes) {
    maxRoute = Math.max(maxRoute, idNum(r.id, 'r-'));
    for (const w of r.waypoints) maxWp = Math.max(maxWp, idNum(w.id, 'wp-'));
  }
  for (const m of marks) maxWp = Math.max(maxWp, idNum(m.id, 'wp-'));
  nextWpId = Math.max(nextWpId, maxWp + 1);
  nextRouteId = Math.max(nextRouteId, maxRoute + 1);
}

/** Read the saved routing slice back into the store. Runs once on boot. */
export function hydrateRouting(): void {
  const def: PersistedRouting = {
    routes: [],
    marks: [],
    activeRouteId: null,
    followRouteId: null,
    activeLegIndex: null,
    followFromLegIndex: null,
    hiddenRouteIds: [],
    destination: null,
  };
  const p = loadJson<PersistedRouting>(ROUTING_LS, def);
  const routes = Array.isArray(p.routes) ? p.routes : [];
  const marks = Array.isArray(p.marks) ? p.marks : [];
  seedIdCounters(routes, marks);

  // Restore the follow state defensively. A saved "following route X on leg N" is
  // only good if route X still exists and still has that leg; otherwise the boat
  // would boot pointed at a leg that's been edited or deleted away. So we keep the
  // follow only when the route is still followable, and clamp the stored leg back
  // into range. Anything that doesn't check out is cleared, not carried stale.
  let followRouteId = p.followRouteId ?? null;
  let activeLegIndex = typeof p.activeLegIndex === 'number' ? p.activeLegIndex : null;
  if (followRouteId) {
    const followed = routes.find((r) => r.id === followRouteId);
    if (!followed || followed.waypoints.length < 2) {
      followRouteId = null;
      activeLegIndex = null;
    } else if (activeLegIndex != null) {
      activeLegIndex = clampLegIndex(followed, activeLegIndex);
    }
  } else {
    // No route being followed, so a stray leg index has nothing to sit on.
    activeLegIndex = null;
  }

  useRoutingStore.setState({
    routes,
    marks,
    activeRouteId: p.activeRouteId ?? null,
    followRouteId,
    activeLegIndex,
    followFromLegIndex: typeof p.followFromLegIndex === 'number' ? p.followFromLegIndex : null,
    hiddenRouteIds: Array.isArray(p.hiddenRouteIds) ? p.hiddenRouteIds : [],
    destination: p.destination ?? null,
  });
}

// Hydrate BEFORE we start listening, so restoring the saved slice on boot
// doesn't immediately write it straight back. After that, persist on any change.
hydrateRouting();
useRoutingStore.subscribe((s) => saveJson(ROUTING_LS, snapshotRouting(s)));

/** Convenience selector, returns the active route, or null. */
export function selectActiveRoute(state: ReturnType<typeof useRoutingStore.getState>): import('@/routing').Route | null {
  if (!state.activeRouteId) return null;
  return state.routes.find((r) => r.id === state.activeRouteId) ?? null;
}

/**
 * The route we're actively building (the one the HUD reads its running total and
 * TTG from), or null when we're not in build mode. Picks by id off the stable
 * `routes` slice, so a component can select `buildingRouteId` and `routes`
 * separately and derive this with useMemo, rather than the store handing back a
 * fresh object each render (which would defeat zustand's reference equality and
 * loop the render).
 */
export function selectBuildingRoute(
  state: Pick<RoutingState, 'buildingRouteId' | 'routes'>,
): import('@/routing').Route | null {
  if (!state.buildingRouteId) return null;
  return state.routes.find((r) => r.id === state.buildingRouteId) ?? null;
}

/** The leg metrics for whatever we are navigating to (a followed route, or a
 * goto mark). One shape so the data fields and active-leg line don't care which. */
export interface ActiveLeg {
  /** The point we are making for, [lon, lat]. */
  target: [number, number];
  /** Distance to that point, nm. */
  dtgNm: number;
  /** Bearing from the boat to that point, degrees true. */
  brgDeg: number;
  /** Cross-track error against the leg, nm (signed: + = right of track). */
  xteNm: number;
  /** Distance remaining to the end of the route (or the mark), nm. */
  remainingNm: number;
}

export function selectActiveLeg(
  state: Pick<RoutingState, 'followRouteId' | 'activeLegIndex' | 'followFromLegIndex' | 'goto' | 'routes' | 'marks'>,
  boat: [number, number] | null,
): ActiveLeg | null {
  if (!boat) return null;
  if (state.followRouteId) {
    const route = state.routes.find((r) => r.id === state.followRouteId);
    if (route && route.waypoints.length >= 2) {
      // Authoritative path: a route activated for follow carries the leg we're
      // steering, so read the metrics straight off that stored leg (clamped in
      // case an edit left it stale). Falls through to the old nearest-leg pick
      // when there's no stored index (legacy follow, or state from an old build).
      // The approach leg: making straight for WP1 from wherever we are.
      if (state.activeLegIndex === APPROACH_LEG_INDEX) {
        const a = approachLeg(route, boat);
        if (a) {
          return {
            target: a.to,
            dtgNm: a.distanceToGoNm,
            brgDeg: a.bearingDeg,
            xteNm: a.xteNm,
            remainingNm: a.remainingNm,
          };
        }
      }
      if (state.activeLegIndex != null) {
        const f = followLeg(route, boat, clampLegIndex(route, state.activeLegIndex));
        if (f) {
          return {
            target: f.to,
            dtgNm: f.distanceToGoNm,
            brgDeg: f.bearingDeg,
            xteNm: f.xteNm,
            remainingNm: f.remainingNm,
          };
        }
      }
      const m = monitorRoute(route, boat, state.followFromLegIndex ?? 0);
      const idx = m.activeLegIndex ?? 0;
      const target = (route.waypoints[idx + 1] ?? route.waypoints[route.waypoints.length - 1]).position;
      return {
        target,
        dtgNm: m.active?.distanceToGoNm ?? 0,
        brgDeg: rhumbBearingDeg(boat, target),
        xteNm: m.active?.xteNm ?? 0,
        remainingNm: m.remainingNm ?? 0,
      };
    }
  }
  if (state.goto) {
    const goto = state.goto;
    const mark = state.marks.find((mk) => mk.id === goto.markId);
    if (mark) {
      const dtr = distanceToRun(boat, goto.from, mark.position);
      const dtg = rhumbDistanceNm(boat, mark.position);
      return {
        target: mark.position,
        dtgNm: dtg,
        brgDeg: rhumbBearingDeg(boat, mark.position),
        xteNm: dtr.xteNm,
        remainingNm: dtg,
      };
    }
  }
  return null;
}
